import { autocompletion, type CompletionContext, closeBrackets } from '@codemirror/autocomplete'
import { defaultKeymap, history, indentWithTab, redo, undo } from '@codemirror/commands'
import { json, jsonParseLinter } from '@codemirror/lang-json'
import { python } from '@codemirror/lang-python'
import { xml } from '@codemirror/lang-xml'
import { bracketMatching, HighlightStyle, syntaxHighlighting } from '@codemirror/language'
import { type Diagnostic, type LintSource, linter, lintGutter } from '@codemirror/lint'
import { EditorState } from '@codemirror/state'
import {
  type Command,
  EditorView,
  highlightWhitespace,
  type KeyBinding,
  keymap,
  lineNumbers,
} from '@codemirror/view'
import { tags } from '@lezer/highlight'
import { cn } from '@neo/lib/utils'
import React from 'react'
import { logger } from 'v2/utils/logger'

type Language = 'json' | 'python' | 'xml'

// Highlight classes follow the active theme without replacing the editor state,
// so switching appearance preserves the selection, undo history and suggestions.
const editorHighlightStyle = HighlightStyle.define([
  { tag: [tags.string, tags.attributeValue], class: 'text-brand-text' },
  { tag: [tags.propertyName, tags.tagName, tags.attributeName], class: 'text-foreground' },
  { tag: [tags.keyword, tags.bool, tags.null, tags.number], class: 'text-foreground text-xs-500' },
  { tag: [tags.punctuation, tags.operator, tags.meta], class: 'text-secondary' },
  { tag: tags.comment, class: 'text-secondary italic' },
  { tag: tags.invalid, class: 'text-status-danger' },
])

interface CodeEditorProps {
  readOnly?: boolean
  value: string
  onChange: (value: string) => void
  className?: string
  suggestions?: string[]
  onError?: (errorCount: number, diagnostics: Diagnostic[]) => void
  errorTexts?: { words: string[]; message: string }[]
  language?: Language
  showLineNumbers?: boolean
  lineWrapping?: boolean
  isKeyValueEditor?: boolean
  allowNestedValues?: boolean
}

const generateSuggestions = (options: string[], context: CompletionContext) => {
  const word = context.matchBefore(/{{[\w-]*/)
  const pipe = context.matchBefore(/\|[\w-]*/)
  if (!(word || pipe) && !context.explicit) return null

  let from = context.pos
  if (pipe) from = pipe.from + 1
  else if (word?.from) from = word.from + 2

  return {
    from,
    options: options.map((option) => ({
      label: option,
      type: option.startsWith('system') ? 'keyword' : 'variable',
      apply: `${option}${pipe ? '' : '}}'}`,
    })),
  }
}

const customJSONLinter = (
  isKeyValueEditor: boolean,
  allowNestedValues: boolean,
  onError: (errorCount: number, diagnostics: Diagnostic[]) => void,
  options: string[] | undefined,
  _errorFunc: () => Diagnostic[],
  view: EditorView,
) => {
  const diagnostic = jsonParseLinter()(view)
  if (isKeyValueEditor && !allowNestedValues && !diagnostic.length) {
    try {
      const jsonData = JSON.parse(view.state.doc.toString())
      Object.keys(jsonData).forEach((key) => {
        if (typeof jsonData[key] !== 'string') {
          const pos = view.state.doc.toString().indexOf(`"${key}":`)
          diagnostic.push({
            from: pos + 1,
            message: 'Nested values are not allowed here',
            severity: 'error',
            to: pos + key.length + 1,
          })
        }
      })
    } catch (e) {
      logger.warn('Error parsing JSON', { error: e })
      // JSON parse error will be handled by jsonParseLinter
    }
  }
  if (!diagnostic.length && options?.length) {
    // const jsonString = view.state.doc.toString()
    // const errorTexts = errorFunc()
    // Implement checkVariablesInString function or similar logic here
    // checkVariablesInString(jsonString, options, diagnostic, errorTexts)
  }
  onError(diagnostic.length, diagnostic)
  return diagnostic
}

export const CodeEditor: React.FC<CodeEditorProps> = ({
  readOnly = false,
  value,
  onChange,
  className,
  suggestions,
  onError = () => {},
  errorTexts = [],
  language = 'json',
  showLineNumbers = true,
  lineWrapping = false,
  isKeyValueEditor = false,
  allowNestedValues = false,
}) => {
  const options = suggestions?.map((v: string) => v.replace(/^{{|}}$/g, ''))
  const editorRef = React.useRef<HTMLDivElement>(null)
  const viewRef = React.useRef<EditorView | null>(null)
  const errorTextRef = React.useRef(errorTexts)
  const internalValueRef = React.useRef(value)
  const isInternalChangeRef = React.useRef(false)

  errorTextRef.current = errorTexts

  const errorFunc = (): Diagnostic[] =>
    errorTextRef.current.map(({ message }) => ({
      from: 0,
      to: 0,
      severity: 'error',
      message,
    }))

  // Initialize editor only once
  React.useEffect(() => {
    if (!editorRef.current || viewRef.current) return

    const setupEditor = () => {
      const getExtensions = (lang: Language) => {
        const baseExtensions = [
          history(),
          keymap.of([
            ...(defaultKeymap as KeyBinding[]),
            indentWithTab as KeyBinding,
            { key: 'Mod-z', run: undo as unknown as Command, preventDefault: true },
            { key: 'Mod-Shift-z', run: redo as unknown as Command, preventDefault: true },
          ]),
          ...(showLineNumbers ? [lineNumbers()] : []),
          ...(showLineNumbers ? [lintGutter()] : []),
          bracketMatching(),
          closeBrackets(),
          syntaxHighlighting(editorHighlightStyle),
          ...(lineWrapping || lang === 'xml' ? [EditorView.lineWrapping] : []),
          EditorView.updateListener.of((update) => {
            if (update.docChanged) {
              isInternalChangeRef.current = true
              internalValueRef.current = update.state.doc.toString()
              onChange(internalValueRef.current)
              setTimeout(() => {
                isInternalChangeRef.current = false
              }, 0)
            }
          }),
        ]

        if (lang === 'json') {
          return [
            ...baseExtensions,
            json(),
            linter(
              customJSONLinter.bind(
                null,
                isKeyValueEditor,
                allowNestedValues,
                onError,
                options,
                errorFunc,
              ) as unknown as LintSource,
            ),
            autocompletion({
              override: options ? [generateSuggestions.bind(null, options)] : undefined,
            }),
            ...(readOnly ? [EditorState.readOnly.of(true)] : []),
          ]
        }
        if (lang === 'xml') {
          return [
            ...baseExtensions,
            xml(),
            autocompletion(),
            ...(readOnly ? [EditorState.readOnly.of(true)] : []),
          ]
        }
        return [
          ...baseExtensions,
          python(),
          autocompletion(),
          highlightWhitespace(),
          ...(readOnly ? [EditorState.readOnly.of(true)] : []),
        ]
      }

      const state = EditorState.create({
        doc: value,
        extensions: getExtensions(language),
      })

      viewRef.current = new EditorView({
        state,
        parent: editorRef.current as Element,
      })
    }

    setupEditor()

    return () => {
      if (viewRef.current) {
        viewRef.current.destroy()
        viewRef.current = null
      }
    }
  }, []) // Only run once on mount

  // Handle external value changes
  React.useEffect(() => {
    if (isInternalChangeRef.current || !viewRef.current) return

    if (value !== internalValueRef.current) {
      internalValueRef.current = value

      viewRef.current.dispatch({
        changes: {
          from: 0,
          to: viewRef.current.state.doc.length,
          insert: value,
        },
      })
    }
  }, [value])

  return (
    // min-w-0 lets this shrink inside narrow flex parents (e.g. sidebars);
    // without it the editor's intrinsic min-content width overflows the container.
    <div className={cn('min-h-[200px] min-w-0 max-w-full flex', className)}>
      <div
        className={cn(
          'bg-bg2 text-foreground min-w-0 max-w-full flex-1 selection:bg-primary/15 selection:text-foreground',
          // CodeMirror injects high-specificity light-theme rules even under .dark.
          // These vendor overrides must win without recreating the editor state.
          '[&_.cm-editor]:bg-bg2 [&_.cm-content]:!caret-foreground [&_.cm-cursor]:!border-l-foreground [&_.cm-dropCursor]:!border-l-foreground [&_.cm-selectionBackground]:!bg-primary/15',
          // Keep only the text scroller clipped; CodeMirror tooltips sit beside it.
          '[&_.cm-editor]:min-w-0 [&_.cm-editor]:max-w-full [&_.cm-editor_.cm-scroller]:min-w-0 [&_.cm-editor_.cm-scroller]:max-w-full [&_.cm-editor_.cm-scroller]:!overflow-x-auto [&_.cm-scroller]:[color-scheme:light] dark:[&_.cm-scroller]:[color-scheme:dark]',
          '[&_.cm-scroller]:!font-jetbrains [&_.cm-scroller]:text-xs-400 [&_.cm-gutters]:!bg-bg2 [&_.cm-gutters]:!text-secondary [&_.cm-gutters]:!border-border',
          '[&_.cm-focused]:!outline-none [&_.cm-focused]:ring-2 [&_.cm-focused]:ring-ring/30 [&_.cm-focused]:ring-inset',
          '[&_.cm-matchingBracket]:!bg-accent [&_.cm-nonmatchingBracket]:!bg-status-danger/15 [&_.cm-nonmatchingBracket]:!text-status-danger',
          '[&_.cm-tooltip]:!bg-popover [&_.cm-tooltip]:!text-popover-foreground [&_.cm-tooltip]:!border-border [&_.cm-tooltip]:font-inter [&_.cm-tooltip]:text-xs-400 [&_.cm-tooltip]:max-w-[calc(100vw-2rem)]',
          '[&_.cm-tooltip-autocomplete_ul]:!font-jetbrains [&_.cm-tooltip_li[aria-selected=true]]:!bg-accent [&_.cm-tooltip_li[aria-selected=true]]:!text-accent-foreground',
          '[&_.cm-snippetField]:!bg-accent [&_.cm-snippetFieldPosition]:!border-l-secondary',
          '[&_.cm-diagnostic]:!border-l [&_.cm-diagnostic-error]:!border-l-status-danger [&_.cm-diagnostic-warning]:!border-l-status-warning [&_.cm-diagnostic-info]:!border-l-status-neutral [&_.cm-diagnostic-hint]:!border-l-status-progress',
          '[&_.cm-diagnosticAction]:!bg-accent [&_.cm-diagnosticAction]:!text-accent-foreground [&_.cm-diagnosticAction:focus-visible]:outline [&_.cm-diagnosticAction:focus-visible]:outline-ring',
          '[&_.cm-lintRange]:!bg-none [&_.cm-lintRange]:underline [&_.cm-lintRange]:decoration-wavy [&_.cm-lintRange]:underline-offset-2 [&_.cm-lintRange-active]:!bg-accent',
          '[&_.cm-lintRange-error]:decoration-status-danger [&_.cm-lintRange-warning]:decoration-status-warning [&_.cm-lintRange-info]:decoration-status-neutral [&_.cm-lintRange-hint]:decoration-status-progress',
          '[&_.cm-lint-marker]:!content-none [&_.cm-lint-marker]:!size-2 [&_.cm-lint-marker]:rounded-full [&_.cm-lint-marker]:mx-auto [&_.cm-lint-marker]:mt-1',
          '[&_.cm-lint-marker-error]:bg-status-danger [&_.cm-lint-marker-warning]:bg-status-warning [&_.cm-lint-marker-info]:bg-status-neutral [&_.cm-lint-marker-hint]:bg-status-progress',
          '[&_.cm-lintPoint::after]:!border-b-status-danger [&_.cm-lintPoint.cm-lintPoint-warning::after]:!border-b-status-warning [&_.cm-lintPoint.cm-lintPoint-info::after]:!border-b-status-neutral [&_.cm-lintPoint.cm-lintPoint-hint::after]:!border-b-status-progress',
        )}
        ref={editorRef}
      />
    </div>
  )
}
